/**
 * ForexRates - Live Forex Market Rates Dashboard
 * Features: Real-time rates, Rate Alerts, Historical Charts
 */
import { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  Search, 
  RefreshCw, 
  Clock,
  Globe,
  DollarSign,
  Bell,
  BellRing,
  Plus,
  Trash2,
  LineChart,
  X,
  ChevronUp,
  ChevronDown,
  Target
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import api, { 
  createRateAlert, 
  getRateAlerts, 
  deleteRateAlert, 
  toggleRateAlert,
  getHistoricalRates 
} from '../lib/api';

const REGIONS = [
  { id: 'all', name: 'All', icon: Globe },
  { id: 'major', name: 'Major' },
  { id: 'africa', name: 'Africa' },
  { id: 'europe', name: 'Europe' },
  { id: 'americas', name: 'Americas' },
  { id: 'asia_pacific', name: 'Asia/Pacific' },
];

const ForexRates = () => {
  const { t } = useTranslation();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [source, setSource] = useState('');
  const [activeRegion, setActiveRegion] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('rates');
  
  // Rate Alerts State
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [showCreateAlert, setShowCreateAlert] = useState(false);
  const [newAlert, setNewAlert] = useState({
    from_currency: 'USD',
    to_currency: 'EUR',
    target_rate: '',
    condition: 'below',
    notification_method: 'both',
    note: ''
  });
  
  // Historical Chart State
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [historicalData, setHistoricalData] = useState(null);
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const [chartDays, setChartDays] = useState(30);

  // Fetch rates on mount
  useEffect(() => {
    fetchRates();
  }, []);
  
  // Fetch alerts when tab changes to alerts
  useEffect(() => {
    if (activeTab === 'alerts') {
      fetchAlerts();
    }
  }, [activeTab]);

  const fetchRates = async (showToast = false) => {
    try {
      setRefreshing(true);
      const response = await api.get('/forex/rates');
      setRates(response.data.rates || []);
      setLastUpdated(response.data.last_updated);
      setSource(response.data.source);
      if (showToast) {
        toast.success('Rates refreshed successfully');
      }
    } catch (error) {
      console.error('Failed to fetch forex rates:', error);
      toast.error('Failed to fetch forex rates');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  const fetchAlerts = async () => {
    try {
      setAlertsLoading(true);
      const response = await getRateAlerts();
      setAlerts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      // Don't show error toast for 401 (unauthenticated) - expected for non-logged in users
      if (error.response?.status !== 401) {
        toast.error('Failed to fetch rate alerts');
      }
    } finally {
      setAlertsLoading(false);
    }
  };
  
  const handleCreateAlert = async () => {
    if (!newAlert.target_rate || parseFloat(newAlert.target_rate) <= 0) {
      toast.error('Please enter a valid target rate');
      return;
    }
    
    try {
      await createRateAlert({
        ...newAlert,
        target_rate: parseFloat(newAlert.target_rate)
      });
      toast.success('Rate alert created!');
      setShowCreateAlert(false);
      setNewAlert({
        from_currency: 'USD',
        to_currency: 'EUR',
        target_rate: '',
        condition: 'below',
        notification_method: 'both',
        note: ''
      });
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create alert');
    }
  };
  
  const handleDeleteAlert = async (alertId) => {
    try {
      await deleteRateAlert(alertId);
      toast.success('Alert deleted');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };
  
  const handleToggleAlert = async (alertId) => {
    try {
      await toggleRateAlert(alertId);
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to toggle alert');
    }
  };
  
  const fetchHistoricalData = async (currencyCode, days = 30) => {
    try {
      setHistoricalLoading(true);
      const response = await getHistoricalRates(currencyCode, days);
      setHistoricalData(response.data);
    } catch (error) {
      console.error('Failed to fetch historical data:', error);
      toast.error('Failed to load historical data');
    } finally {
      setHistoricalLoading(false);
    }
  };
  
  const openHistoricalChart = (currency) => {
    setSelectedCurrency(currency);
    setChartDays(30);
    fetchHistoricalData(currency.code, 30);
  };
  
  const closeHistoricalChart = () => {
    setSelectedCurrency(null);
    setHistoricalData(null);
  };
  
  const handleChartDaysChange = (days) => {
    setChartDays(days);
    if (selectedCurrency) {
      fetchHistoricalData(selectedCurrency.code, days);
    }
  };

  // Filter rates by region and search
  const filteredRates = useMemo(() => {
    let filtered = [...rates];
    
    if (activeRegion !== 'all') {
      if (activeRegion === 'europe') {
        const europeCodes = ['EUR', 'GBP', 'CHF', 'SEK', 'NOK', 'DKK', 'PLN'];
        filtered = filtered.filter(r => europeCodes.includes(r.code));
      } else if (activeRegion === 'americas') {
        const americasCodes = ['USD', 'CAD', 'BRL', 'MXN'];
        filtered = filtered.filter(r => americasCodes.includes(r.code));
      } else if (activeRegion === 'asia_pacific') {
        const asiaCodes = ['JPY', 'CNY', 'AUD', 'SGD', 'HKD', 'INR', 'AED'];
        filtered = filtered.filter(r => asiaCodes.includes(r.code));
      } else {
        filtered = filtered.filter(r => r.region === activeRegion);
      }
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.code.toLowerCase().includes(query) || 
        r.name.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [rates, activeRegion, searchQuery]);

  const formatLastUpdated = (isoString) => {
    if (!isoString) return 'Unknown';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const formatRate = (rate, code) => {
    if (code === 'USD') return '1.0000';
    if (rate >= 1000) return rate.toLocaleString('en-US', { maximumFractionDigits: 2 });
    if (rate >= 100) return rate.toFixed(2);
    if (rate >= 1) return rate.toFixed(4);
    return rate.toFixed(6);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="forex-rates-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-heading flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-emerald-500" />
              Live Forex Market
            </h1>
            <p className="text-muted-foreground mt-1 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Last updated: {formatLastUpdated(lastUpdated)}
              {source === 'fallback' && (
                <span className="text-amber-500 text-xs ml-2">(Fallback rates)</span>
              )}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => fetchRates(true)}
            disabled={refreshing}
            className="gap-2"
            data-testid="refresh-rates-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh Rates
          </Button>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 md:w-auto md:inline-grid" data-testid="main-tabs">
            <TabsTrigger value="rates" className="gap-2">
              <Globe className="w-4 h-4" />
              Live Rates
            </TabsTrigger>
            <TabsTrigger value="alerts" className="gap-2">
              <Bell className="w-4 h-4" />
              Rate Alerts
              {alerts.filter(a => a.is_active).length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                  {alerts.filter(a => a.is_active).length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Live Rates Tab */}
          <TabsContent value="rates" className="space-y-4 mt-4">
            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Region Tabs */}
                  <Tabs value={activeRegion} onValueChange={setActiveRegion} className="w-full md:w-auto">
                    <TabsList className="grid grid-cols-3 md:grid-cols-6 w-full md:w-auto" data-testid="region-tabs">
                      {REGIONS.map(region => (
                        <TabsTrigger 
                          key={region.id} 
                          value={region.id}
                          className="text-xs md:text-sm"
                          data-testid={`region-tab-${region.id}`}
                        >
                          {region.name}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>

                  {/* Search */}
                  <div className="relative flex-1 md:max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search currency..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="currency-search"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Rates Table */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Exchange Rates vs USD</CardTitle>
                <CardDescription>
                  Showing {filteredRates.length} currencies • Click any row to view historical chart
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="rates-table">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground">Currency</th>
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground hidden md:table-cell">Name</th>
                          <th className="text-right py-3 px-4 font-medium text-muted-foreground">Price (USD)</th>
                          <th className="text-right py-3 px-4 font-medium text-muted-foreground">24h Change</th>
                          <th className="text-center py-3 px-4 font-medium text-muted-foreground hidden md:table-cell">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <AnimatePresence>
                          {filteredRates.map((rate, index) => (
                            <motion.tr
                              key={rate.code}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0 }}
                              transition={{ delay: index * 0.02 }}
                              className="border-b last:border-0 hover:bg-muted/50 transition-colors cursor-pointer"
                              onClick={() => openHistoricalChart(rate)}
                              data-testid={`rate-row-${rate.code}`}
                            >
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-3">
                                  <span className="text-2xl">{rate.flag}</span>
                                  <span className="font-mono font-semibold">{rate.code}</span>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-muted-foreground hidden md:table-cell">
                                {rate.name}
                              </td>
                              <td className="py-3 px-4 text-right font-mono font-medium">
                                {formatRate(rate.rate, rate.code)}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <div className={`flex items-center justify-end gap-1 font-mono text-sm ${
                                  rate.change_24h > 0 
                                    ? 'text-emerald-500' 
                                    : rate.change_24h < 0 
                                      ? 'text-red-500' 
                                      : 'text-muted-foreground'
                                }`}>
                                  {rate.change_24h > 0 ? (
                                    <TrendingUp className="w-4 h-4" />
                                  ) : rate.change_24h < 0 ? (
                                    <TrendingDown className="w-4 h-4" />
                                  ) : (
                                    <Minus className="w-4 h-4" />
                                  )}
                                  <span>
                                    {rate.change_24h > 0 ? '+' : ''}{rate.change_24h.toFixed(2)}%
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-center hidden md:table-cell">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openHistoricalChart(rate);
                                  }}
                                  className="gap-1"
                                >
                                  <LineChart className="w-4 h-4" />
                                  Chart
                                </Button>
                              </td>
                            </motion.tr>
                          ))}
                        </AnimatePresence>
                      </tbody>
                    </table>

                    {filteredRates.length === 0 && (
                      <div className="text-center py-12 text-muted-foreground">
                        <Globe className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No currencies found matching your criteria</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rate Alerts Tab */}
          <TabsContent value="alerts" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <BellRing className="w-5 h-5 text-amber-500" />
                      Rate Alerts
                    </CardTitle>
                    <CardDescription>
                      Get notified when currencies hit your target rates
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowCreateAlert(true)} className="gap-2" data-testid="create-alert-btn">
                    <Plus className="w-4 h-4" />
                    Create Alert
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {alertsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : alerts.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="mb-2">No rate alerts yet</p>
                    <p className="text-sm">Create an alert to get notified when rates hit your target</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {alerts.map((alert) => (
                      <div
                        key={alert.id}
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          alert.is_triggered 
                            ? 'bg-emerald-500/10 border-emerald-500/30' 
                            : alert.is_active 
                              ? 'bg-muted/50' 
                              : 'bg-muted/20 opacity-60'
                        }`}
                        data-testid={`alert-${alert.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-semibold">
                              {alert.from_currency}/{alert.to_currency}
                            </span>
                            {alert.is_triggered && (
                              <Badge variant="default" className="bg-emerald-500">
                                Triggered
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              {alert.condition === 'above' ? (
                                <ChevronUp className="w-4 h-4 text-emerald-500" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-red-500" />
                              )}
                              Target: <span className="font-mono">{alert.target_rate.toFixed(4)}</span>
                            </span>
                            {alert.current_rate && (
                              <span className="text-xs">
                                Current: <span className="font-mono">{alert.current_rate.toFixed(4)}</span>
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={alert.is_active}
                            onCheckedChange={() => handleToggleAlert(alert.id)}
                            data-testid={`toggle-alert-${alert.id}`}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteAlert(alert.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                            data-testid={`delete-alert-${alert.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer Note */}
        <div className="text-center text-sm text-muted-foreground py-4 border-t">
          <p>
            Powered by <span className="font-semibold text-emerald-600">ParachPay</span> | 
            {' '}<span data-testid="forex-convert-fee-note">{t('forex.convertFeeNote')}</span> | 
            Cached for 1 hour
          </p>
        </div>
      </div>

      {/* Create Alert Dialog */}
      <Dialog open={showCreateAlert} onOpenChange={setShowCreateAlert}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-500" />
              Create Rate Alert
            </DialogTitle>
            <DialogDescription>
              Get notified when the exchange rate hits your target
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From Currency</Label>
                <Select 
                  value={newAlert.from_currency} 
                  onValueChange={(v) => setNewAlert(prev => ({ ...prev, from_currency: v }))}
                >
                  <SelectTrigger data-testid="from-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {rates.map(r => (
                      <SelectItem key={r.code} value={r.code}>
                        {r.flag} {r.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>To Currency</Label>
                <Select 
                  value={newAlert.to_currency} 
                  onValueChange={(v) => setNewAlert(prev => ({ ...prev, to_currency: v }))}
                >
                  <SelectTrigger data-testid="to-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {rates.map(r => (
                      <SelectItem key={r.code} value={r.code}>
                        {r.flag} {r.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Target Rate</Label>
              <Input
                type="number"
                step="0.0001"
                placeholder="e.g., 0.9200"
                value={newAlert.target_rate}
                onChange={(e) => setNewAlert(prev => ({ ...prev, target_rate: e.target.value }))}
                data-testid="target-rate-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Alert When</Label>
              <Select 
                value={newAlert.condition} 
                onValueChange={(v) => setNewAlert(prev => ({ ...prev, condition: v }))}
              >
                <SelectTrigger data-testid="condition-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="below">Rate goes BELOW target</SelectItem>
                  <SelectItem value="above">Rate goes ABOVE target</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notification Method</Label>
              <Select 
                value={newAlert.notification_method} 
                onValueChange={(v) => setNewAlert(prev => ({ ...prev, notification_method: v }))}
              >
                <SelectTrigger data-testid="notification-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Email & Push</SelectItem>
                  <SelectItem value="email">Email Only</SelectItem>
                  <SelectItem value="push">Push Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Note (optional)</Label>
              <Input
                placeholder="Reminder note for this alert"
                value={newAlert.note}
                onChange={(e) => setNewAlert(prev => ({ ...prev, note: e.target.value }))}
                data-testid="alert-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateAlert(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateAlert} data-testid="confirm-create-alert">
              Create Alert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Historical Chart Dialog */}
      <Dialog open={!!selectedCurrency} onOpenChange={() => closeHistoricalChart()}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="text-3xl">{selectedCurrency?.flag}</span>
              <div>
                <span className="font-mono">{selectedCurrency?.code}/USD</span>
                <p className="text-sm font-normal text-muted-foreground">{selectedCurrency?.name}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {/* Period Selector */}
          <div className="flex gap-2 mb-4">
            {[7, 14, 30].map((days) => (
              <Button
                key={days}
                variant={chartDays === days ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleChartDaysChange(days)}
                data-testid={`chart-days-${days}`}
              >
                {days}D
              </Button>
            ))}
          </div>
          
          {historicalLoading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : historicalData ? (
            <div className="space-y-4">
              {/* Chart */}
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={historicalData.historical}>
                    <defs>
                      <linearGradient id="colorRate" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#888" 
                      tick={{ fill: '#888', fontSize: 12 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      }}
                    />
                    <YAxis 
                      stroke="#888" 
                      tick={{ fill: '#888', fontSize: 12 }}
                      domain={['auto', 'auto']}
                      tickFormatter={(value) => value.toFixed(4)}
                    />
                    <Tooltip
                      contentStyle={{ 
                        backgroundColor: '#1a1a2e', 
                        border: '1px solid #333',
                        borderRadius: '8px'
                      }}
                      labelStyle={{ color: '#888' }}
                      formatter={(value) => [value.toFixed(6), 'Rate']}
                      labelFormatter={(label) => new Date(label).toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="rate" 
                      stroke="#10b981" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorRate)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              
              {/* Statistics */}
              {historicalData.statistics && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Period Change</p>
                    <p className={`font-mono font-bold ${
                      historicalData.statistics.period_change_percent >= 0 
                        ? 'text-emerald-500' 
                        : 'text-red-500'
                    }`}>
                      {historicalData.statistics.period_change_percent >= 0 ? '+' : ''}
                      {historicalData.statistics.period_change_percent.toFixed(2)}%
                    </p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Average</p>
                    <p className="font-mono font-bold">{historicalData.statistics.average.toFixed(4)}</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Low</p>
                    <p className="font-mono font-bold text-red-500">{historicalData.statistics.min.toFixed(4)}</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">High</p>
                    <p className="font-mono font-bold text-emerald-500">{historicalData.statistics.max.toFixed(4)}</p>
                  </div>
                </div>
              )}
              
              {/* Create Alert Button */}
              <Button 
                className="w-full gap-2" 
                variant="outline"
                onClick={() => {
                  setNewAlert(prev => ({
                    ...prev,
                    from_currency: 'USD',
                    to_currency: selectedCurrency.code,
                    target_rate: selectedCurrency.rate.toFixed(4)
                  }));
                  closeHistoricalChart();
                  setShowCreateAlert(true);
                }}
              >
                <Bell className="w-4 h-4" />
                Create Alert for {selectedCurrency?.code}
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ForexRates;
